import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Clock, Palette, Trash2, Plus, Calendar as CalIcon, X, Sun, UserCog, Pencil } from "lucide-react";

const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const INPUT_CLS = "w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none disabled:opacity-60";

function useTrainers() {
  const [trainers, setTrainers] = useState([]);
  const reload = () => api.get("/trainers").then(r => setTrainers(r.data)).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { reload(); }, []);
  return [trainers, reload];
}

function TrainerSelect({ trainers, value, onChange, allowAll, testid, disabled }) {
  return (
    <select data-testid={testid} value={value || ""} disabled={disabled} onChange={e => onChange(e.target.value)} className={INPUT_CLS}>
      {allowAll && <option value="" className="bg-[#120B19]">Tutti gli allenatori</option>}
      {trainers.map(t => <option key={t.id} value={t.id} className="bg-[#120B19]">{t.name}</option>)}
    </select>
  );
}

export default function SettingsPage() {
  const [tab, setTab] = useState("hours");
  const tabs = [
    { k: "trainers", l: "Allenatori", i: <UserCog size={14} /> },
    { k: "hours", l: "Orari", i: <Clock size={14} /> },
    { k: "services", l: "Servizi", i: <Palette size={14} /> },
    { k: "festivita", l: "Festività", i: <Sun size={14} /> },
    { k: "ferie", l: "Ferie", i: <CalIcon size={14} /> },
  ];
  return (
    <div>
      <div className="inline-flex border border-white/10 mb-6 flex-wrap">
        {tabs.map((t, i) => (
          <button key={t.k} data-testid={`settings-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`px-4 py-2 flex items-center gap-2 font-heading uppercase tracking-widest text-xs transition-colors ${i > 0 ? "border-l border-white/10" : ""} ${tab === t.k ? "bg-[#A855F7] text-white" : "text-white/60 hover:text-white"}`}>
            {t.i} {t.l}
          </button>
        ))}
      </div>
      {tab === "trainers" && <TrainersTab />}
      {tab === "hours" && <HoursTab />}
      {tab === "services" && <ServicesTab />}
      {tab === "festivita" && <FestivitaTab />}
      {tab === "ferie" && <FerieTab />}
    </div>
  );
}

function TrainersTab() {
  const [trainers, reload] = useTrainers();
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const add = async () => {
    if (!name.trim()) { toast.error("Inserisci il nome"); return; }
    try { await api.post("/trainers", { name }); toast.success("Allenatore aggiunto"); setName(""); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const rename = async () => {
    try { await api.patch(`/trainers/${editing.id}`, { name: editing.name }); toast.success("Allenatore aggiornato"); setEditing(null); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (t) => {
    if (!window.confirm(`Eliminare l'allenatore "${t.name}"?`)) return;
    try { await api.delete(`/trainers/${t.id}`); toast.success("Allenatore eliminato"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div>
      <div className="label-caps mb-2">Sottomenù</div>
      <h2 className="font-heading font-black uppercase tracking-tighter text-2xl mb-4">Allenatori</h2>
      <p className="text-white/50 text-sm mb-6 max-w-3xl">Ogni servizio è assegnato a un allenatore. Allenatori diversi possono avere slot in parallelo nello stesso orario; lo stesso allenatore no.</p>
      <div className="card-surface p-4 md:p-6 max-w-3xl">
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <input placeholder="Nome allenatore" value={name} onChange={e => setName(e.target.value)} data-testid="trainer-name-input" className={INPUT_CLS} />
          <button onClick={add} data-testid="trainer-add-btn" className="btn-primary flex items-center gap-2 shrink-0"><Plus size={14} /> Aggiungi</button>
        </div>
        <div className="divide-y divide-white/5">
          {trainers.map(t => (
            <div key={t.id} data-testid={`trainer-row-${t.id}`} className="flex flex-col md:flex-row md:items-center justify-between gap-3 py-3">
              {editing?.id === t.id ? (
                <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid={`trainer-edit-input-${t.id}`} className={INPUT_CLS} />
              ) : (
                <div className="min-w-0">
                  <div className="font-heading font-bold uppercase tracking-tight flex items-center gap-2">
                    {t.name}{t.is_default && <span className="text-[9px] px-1.5 py-0.5 bg-[#A855F7]/20 text-[#A855F7] tracking-widest">PREDEFINITO</span>}
                  </div>
                  <div className="text-xs text-white/50 mt-0.5">{t.services?.length ? `${t.services.length} serviz${t.services.length === 1 ? "io" : "i"} assegnat${t.services.length === 1 ? "o" : "i"}` : "Nessun servizio assegnato"}</div>
                </div>
              )}
              <div className="flex gap-3 shrink-0">
                {editing?.id === t.id ? (
                  <>
                    <button onClick={() => setEditing(null)} className="btn-ghost !py-1.5 !text-[11px]">Annulla</button>
                    <button onClick={rename} data-testid={`trainer-save-${t.id}`} className="btn-primary !py-1.5 !text-[11px]">Salva</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => setEditing({ id: t.id, name: t.name })} data-testid={`trainer-edit-${t.id}`} className="text-white/70 hover:text-white text-xs flex items-center gap-1"><Pencil size={12} /> Modifica</button>
                    {!t.is_default && (
                      <button onClick={() => del(t)} data-testid={`trainer-delete-${t.id}`} className="text-[#FF3366]/80 hover:text-[#FF3366] text-xs flex items-center gap-1"><Trash2 size={12} /> Elimina</button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HoursTab() {
  const [trainers] = useTrainers();
  const [tid, setTid] = useState("sara");
  const [hours, setHours] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get(`/settings?trainer_id=${tid}`); setHours(data.working_hours || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tid]);
  const patch = (idx, upd) => setHours(hours.map((h, i) => i === idx ? { ...h, ...upd } : h));
  const save = async () => {
    setBusy(true);
    try { await api.put(`/settings?trainer_id=${tid}`, { working_hours: hours }); toast.success("Orari salvati"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <div className="label-caps mb-2">Sottomenù</div>
      <h2 className="font-heading font-black uppercase tracking-tighter text-2xl mb-4">Orari di lavoro</h2>
      <div className="max-w-xs mb-6">
        <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Allenatore</span>
        <TrainerSelect trainers={trainers} value={tid} onChange={setTid} testid="hours-trainer-select" />
      </div>
      <div className="card-surface p-6 space-y-3 max-w-4xl">
        {hours.map((h, i) => (
          <div key={i} data-testid={`day-${i}`} className="grid grid-cols-1 md:grid-cols-[130px_120px_1fr_1fr_1fr_1fr] gap-3 items-center border-b border-white/5 pb-3 last:border-0 last:pb-0">
            <div className="font-heading font-bold uppercase tracking-tight">{DAYS[i]}</div>
            <label className="flex items-center gap-2 text-sm text-white/70">
              <input type="checkbox" checked={!h.closed} onChange={e => patch(i, { closed: !e.target.checked })} data-testid={`open-${i}`} />
              {h.closed ? "Chiuso" : "Aperto"}
            </label>
            <TimeInput label="Apre" value={h.open} disabled={h.closed} onChange={v => patch(i, { open: v })} />
            <TimeInput label="Chiude" value={h.close} disabled={h.closed} onChange={v => patch(i, { close: v })} />
            <TimeInput label="Pausa da" value={h.lunch_from} disabled={h.closed} onChange={v => patch(i, { lunch_from: v })} />
            <TimeInput label="Pausa a" value={h.lunch_to} disabled={h.closed} onChange={v => patch(i, { lunch_to: v })} />
          </div>
        ))}
        <div className="pt-3">
          <button disabled={busy} onClick={save} data-testid="save-settings" className="btn-primary flex items-center gap-2">
            <Save size={14} /> {busy ? "Salvo…" : "Salva orari"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ServicesTab() {
  const [trainers] = useTrainers();
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null); // null | {key, label, capacity, duration, color, description}
  const [conflicts, setConflicts] = useState(null);

  const PALETTE = [
    "#A855F7", "#EC4899", "#F97316", "#F59E0B", "#EAB308",
    "#84CC16", "#10B981", "#14B8A6", "#06B6D4", "#3B82F6",
    "#6366F1", "#8B5CF6", "#D946EF", "#FF3366", "#6E667A",
  ];

  const load = async () => {
    try { const { data } = await api.get("/services"); setServices(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    services.forEach(s => document.documentElement.style.setProperty(`--svc-${s.key}`, s.color || "#6E667A"));
  }, [services]);

  const usedColors = services.map(s => (s.color || "").toUpperCase());

  const save = async (svc) => {
    try {
      await api.patch(`/services/${svc.key}`, {
        label: svc.label,
        capacity: Number(svc.capacity),
        color: svc.color,
        duration: Number(svc.duration),
        description: svc.description,
        trainer_id: svc.trainer_id,
      });
      toast.success("Servizio aggiornato (applicato solo agli slot futuri)");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const askDelete = async (svc) => {
    try {
      const { data } = await api.get(`/services/${svc.key}/preview-conflicts`);
      if ((data.conflicts || []).length === 0) {
        if (!window.confirm(`Eliminare definitivamente "${svc.label}"?`)) return;
        await api.delete(`/services/${svc.key}`);
        toast.success("Servizio eliminato"); load();
      } else {
        setConflicts({ svc, list: data.conflicts });
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createNew = async () => {
    if (!creating?.label?.trim()) { toast.error("Inserisci un'etichetta"); return; }
    const key = (creating.key || creating.label).toLowerCase().trim().replace(/[^a-z0-9]+/g, "_");
    try {
      await api.post("/services", {
        key, label: creating.label,
        capacity: Number(creating.capacity) || 1,
        color: creating.color || "#A855F7",
        duration: Number(creating.duration) || 60,
        description: creating.description || "",
        trainer_id: creating.trainer_id || "sara",
      });
      toast.success("Servizio creato");
      setCreating(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-caps mb-2">Sottomenù</div>
          <h2 className="font-heading font-black uppercase tracking-tighter text-2xl">Servizi offerti</h2>
        </div>
        <button
          data-testid="add-service-btn"
          onClick={() => setCreating({ key: "", label: "", capacity: 1, duration: 60, trainer_id: "sara", color: PALETTE.find(c => !usedColors.includes(c.toUpperCase())) || "#A855F7", description: "" })}
          className="btn-primary flex items-center gap-2">
          <Plus size={14} /> Aggiungi servizio
        </button>
      </div>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 max-w-5xl">
        {services.map(s => {
          const isEditing = editing?.key === s.key;
          const draft = isEditing ? editing : s;
          return (
            <div key={s.key} data-testid={`service-${s.key}`} className="card-surface p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 shrink-0" style={{ background: draft.color }} />
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <input value={draft.label} onChange={e => setEditing({ ...editing, label: e.target.value })}
                      data-testid={`svc-label-${s.key}`}
                      className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none" />
                  ) : (
                    <div className="font-heading font-bold uppercase tracking-tight">{s.label}</div>
                  )}
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">{s.key}</div>
                </div>
              </div>

              <label className="block mt-3">
                <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Allenatore</span>
                <TrainerSelect trainers={trainers} value={draft.trainer_id} disabled={!isEditing}
                  onChange={v => setEditing({ ...editing, trainer_id: v })} testid={`svc-trainer-${s.key}`} />
              </label>

              <label className="block mt-3">
                <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Descrizione</span>
                <textarea rows={2} value={draft.description || ""} disabled={!isEditing}
                  onChange={e => setEditing({ ...editing, description: e.target.value })}
                  data-testid={`svc-desc-${s.key}`}
                  className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none disabled:opacity-60" />
              </label>

              <div className="grid grid-cols-3 gap-3 mt-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Capienza</span>
                  <input type="number" min="1" value={draft.capacity} disabled={!isEditing}
                    onChange={e => setEditing({ ...editing, capacity: e.target.value })}
                    data-testid={`svc-capacity-${s.key}`}
                    className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none disabled:opacity-60" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Durata (min)</span>
                  <input type="number" min="15" step="15" value={draft.duration || 60} disabled={!isEditing}
                    onChange={e => setEditing({ ...editing, duration: e.target.value })}
                    data-testid={`svc-duration-${s.key}`}
                    className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none disabled:opacity-60" />
                </label>
                <div>
                  <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Colore</span>
                  {isEditing ? (
                    <PaletteRow used={usedColors.filter(c => c !== (s.color || "").toUpperCase())} value={draft.color} onChange={c => setEditing({ ...editing, color: c })} testidPrefix={`svc-color-${s.key}`} />
                  ) : (
                    <div className="w-full h-10 border border-white/15" style={{ background: draft.color }} />
                  )}
                </div>
              </div>
              <div className="mt-3 flex gap-2 justify-end">
                {isEditing ? (
                  <>
                    <button onClick={() => setEditing(null)} className="btn-ghost !py-1.5 !text-[11px]">Annulla</button>
                    <button onClick={() => save(editing)} data-testid={`svc-save-${s.key}`} className="btn-primary !py-1.5 !text-[11px]">Salva</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => askDelete(s)} data-testid={`svc-delete-${s.key}`} className="btn-ghost !py-1.5 !text-[11px] !text-[#FF3366]">
                      <Trash2 size={12} className="inline mr-1" /> Elimina
                    </button>
                    <button data-testid={`svc-edit-${s.key}`} onClick={() => setEditing({ ...s })} className="btn-ghost !py-1.5 !text-[11px]">Modifica</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-white/40 mt-3 max-w-4xl">
        Le modifiche si applicano solo agli slot futuri. Gli slot già conclusi e lo storico mantengono i dati originali.
      </p>

      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setCreating(null)}>
          <div className="bg-[#120B19] border border-white/10 max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="label-caps mb-2">Nuovo servizio</div>
            <h3 className="font-heading font-black uppercase tracking-tight text-xl mb-4">Aggiungi servizio</h3>
            <div className="space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Etichetta</span>
                <input value={creating.label} onChange={e => setCreating({ ...creating, label: e.target.value })}
                  data-testid="new-svc-label"
                  className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Allenatore</span>
                <TrainerSelect trainers={trainers} value={creating.trainer_id} onChange={v => setCreating({ ...creating, trainer_id: v })} testid="new-svc-trainer" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Descrizione</span>
                <textarea rows={2} value={creating.description} onChange={e => setCreating({ ...creating, description: e.target.value })}
                  data-testid="new-svc-desc"
                  className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Capienza</span>
                  <input type="number" min="1" value={creating.capacity} onChange={e => setCreating({ ...creating, capacity: e.target.value })}
                    data-testid="new-svc-capacity"
                    className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Durata (min)</span>
                  <input type="number" min="15" step="15" value={creating.duration} onChange={e => setCreating({ ...creating, duration: e.target.value })}
                    data-testid="new-svc-duration"
                    className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none" />
                </label>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Colore identificativo</span>
                <PaletteRow used={usedColors} value={creating.color} onChange={c => setCreating({ ...creating, color: c })} testidPrefix="new-svc-color" />
                <div className="text-[10px] text-white/40 mt-1">I colori con "×" sono già usati da altri servizi.</div>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-6">
              <button onClick={() => setCreating(null)} className="btn-ghost !py-1.5 !text-[11px]">Annulla</button>
              <button onClick={createNew} data-testid="new-svc-save" className="btn-primary !py-1.5 !text-[11px]">Crea servizio</button>
            </div>
          </div>
        </div>
      )}

      {conflicts && (
        <ConflictModal
          title={`Impossibile eliminare "${conflicts.svc.label}"`}
          subtitle="Ci sono prenotazioni future attive collegate a questo servizio. Gestiscile prima manualmente (annulla o sposta), poi riprova l'eliminazione."
          items={conflicts.list}
          onClose={() => setConflicts(null)}
        />
      )}
    </div>
  );
}

function PaletteRow({ used, value, onChange, testidPrefix }) {
  const PALETTE = [
    "#A855F7", "#EC4899", "#F97316", "#F59E0B", "#EAB308",
    "#84CC16", "#10B981", "#14B8A6", "#06B6D4", "#3B82F6",
    "#6366F1", "#8B5CF6", "#D946EF", "#FF3366", "#6E667A",
  ];
  const usedSet = new Set((used || []).map(c => (c || "").toUpperCase()));
  return (
    <div className="grid grid-cols-8 gap-1.5">
      {PALETTE.map(c => {
        const isUsed = usedSet.has(c.toUpperCase()) && c !== value;
        const isActive = c.toUpperCase() === (value || "").toUpperCase();
        return (
          <button
            key={c}
            type="button"
            data-testid={`${testidPrefix}-${c.replace('#','')}`}
            onClick={() => onChange(c)}
            className={`h-7 relative border-2 transition-all ${isActive ? "border-white scale-110" : "border-transparent hover:border-white/40"} ${isUsed ? "opacity-40" : ""}`}
            style={{ background: c }}
            title={isUsed ? "Già in uso" : c}
          >
            {isUsed && <span className="absolute inset-0 flex items-center justify-center text-white text-[10px] font-bold">×</span>}
          </button>
        );
      })}
    </div>
  );
}

function FestivitaTab() {
  const [trainers] = useTrainers();
  const [ftid, setFtid] = useState("");
  const [items, setItems] = useState([]);
  const [md, setMd] = useState("");
  const [label, setLabel] = useState("");
  const [conflicts, setConflicts] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/festivita"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!md || !/^\d{2}-\d{2}$/.test(md)) { toast.error("Formato MM-DD (es. 25-12)"); return; }
    try {
      await api.post("/festivita", { month_day: md, label, trainer_id: ftid || null });
      toast.success("Festività aggiunta");
      setMd(""); setLabel(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa festività?")) return;
    try { await api.delete(`/festivita/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="label-caps mb-2">Sottomenù</div>
      <h2 className="font-heading font-black uppercase tracking-tighter text-2xl mb-4">Festività ricorrenti</h2>
      <p className="text-white/50 text-sm mb-6 max-w-3xl">Date che si ripetono ogni anno (es. 25 dicembre). Sono automaticamente bloccate per prenotazioni tutti gli anni futuri.</p>

      <div className="card-surface p-4 md:p-6 max-w-3xl">
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <input placeholder="MM-DD (es. 25-12)" value={md} onChange={e => setMd(e.target.value)}
            data-testid="fest-md" className="flex-1 bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none" />
          <input placeholder="Etichetta (Natale)" value={label} onChange={e => setLabel(e.target.value)}
            data-testid="fest-label" className="flex-1 bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none" />
          <div className="md:w-48"><TrainerSelect trainers={trainers} value={ftid} onChange={setFtid} allowAll testid="fest-trainer" /></div>
          <button onClick={add} data-testid="fest-add" className="btn-primary flex items-center gap-2">
            <Plus size={14} /> Aggiungi
          </button>
        </div>

        <div className="divide-y divide-white/5">
          {items.map(f => (
            <div key={f.id} data-testid={`fest-${f.id}`} className="flex items-center justify-between py-3">
              <div>
                <div className="font-heading font-bold uppercase tracking-tight">{f.month_day}</div>
                <div className="text-xs text-white/50">{f.label || "—"} · {f.trainer_name}</div>
              </div>
              <button onClick={() => del(f.id)} className="text-[#FF3366]/80 hover:text-[#FF3366] text-xs flex items-center gap-1">
                <Trash2 size={12} /> Elimina
              </button>
            </div>
          ))}
          {items.length === 0 && <div className="py-8 text-center text-white/40 text-sm">Nessuna festività impostata.</div>}
        </div>
      </div>

      {conflicts && <ConflictModal title="Conflitti da gestire" items={conflicts} onClose={() => setConflicts(null)} />}
    </div>
  );
}

function FerieTab() {
  const [items, setItems] = useState([]);
  const [df, setDf] = useState("");
  const [dt, setDt] = useState("");
  const [reason, setReason] = useState("");
  const [conflictModal, setConflictModal] = useState(null);
  const [trainers] = useTrainers();
  const [ftid, setFtid] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/ferie"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const preview = async () => {
    if (!df || !dt) { toast.error("Seleziona intervallo date"); return; }
    if (df > dt) { toast.error("Data inizio dopo data fine"); return; }
    try {
      const { data } = await api.get(`/ferie/preview-conflicts?date_from=${df}&date_to=${dt}${ftid ? `&trainer_id=${ftid}` : ""}`);
      if ((data.conflicts || []).length > 0) {
        setConflictModal({
          list: data.conflicts,
          onConfirm: doSave,
        });
      } else {
        await doSave();
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doSave = async () => {
    try {
      await api.post("/ferie", { date_from: df, date_to: dt, reason, trainer_id: ftid || null });
      toast.success("Ferie impostate");
      setDf(""); setDt(""); setReason(""); setConflictModal(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo periodo di ferie?")) return;
    try { await api.delete(`/ferie/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="label-caps mb-2">Sottomenù</div>
      <h2 className="font-heading font-black uppercase tracking-tighter text-2xl mb-4">Ferie e chiusure</h2>
      <p className="text-white/50 text-sm mb-6 max-w-3xl">Periodi di chiusura non ricorrenti. Il calendario blocca le nuove prenotazioni in quei giorni.</p>

      <div className="card-surface p-4 md:p-6 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Da</span>
            <input type="date" value={df} onChange={e => setDf(e.target.value)}
              data-testid="ferie-from" className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">A</span>
            <input type="date" value={dt} onChange={e => setDt(e.target.value)}
              data-testid="ferie-to" className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Motivo (opzionale)</span>
            <input value={reason} onChange={e => setReason(e.target.value)}
              data-testid="ferie-reason" className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Allenatore</span>
            <TrainerSelect trainers={trainers} value={ftid} onChange={setFtid} allowAll testid="ferie-trainer" />
          </label>
        </div>
        <button onClick={preview} data-testid="ferie-add" className="btn-primary flex items-center gap-2">
          <Plus size={14} /> Imposta ferie
        </button>

        <div className="divide-y divide-white/5 mt-6">
          {items.map(f => (
            <div key={f.id} data-testid={`ferie-${f.id}`} className="flex items-center justify-between py-3">
              <div>
                <div className="font-heading font-bold uppercase tracking-tight">{f.date_from} → {f.date_to}</div>
                <div className="text-xs text-white/50">{f.reason || "—"} · {f.trainer_name}</div>
              </div>
              <button onClick={() => del(f.id)} className="text-[#FF3366]/80 hover:text-[#FF3366] text-xs flex items-center gap-1">
                <Trash2 size={12} /> Elimina
              </button>
            </div>
          ))}
          {items.length === 0 && <div className="py-8 text-center text-white/40 text-sm">Nessun periodo di ferie impostato.</div>}
        </div>
      </div>

      {conflictModal && (
        <ConflictModal
          title="Prenotazioni nel periodo selezionato"
          subtitle="Ci sono prenotazioni attive nell'intervallo scelto. Conferma per registrare comunque le ferie: dovrai gestirle manualmente (annulla o sposta) dopo l'avviso ai clienti."
          items={conflictModal.list}
          onClose={() => setConflictModal(null)}
          onConfirm={conflictModal.onConfirm}
          confirmLabel="Conferma comunque"
        />
      )}
    </div>
  );
}

function ConflictModal({ title, subtitle, items, onClose, onConfirm, confirmLabel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-[#120B19] border border-white/10 max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="label-caps text-[#FF3366]">Conflitti</div>
            <h3 className="font-heading font-black uppercase tracking-tight text-xl mt-1">{title}</h3>
            {subtitle && <p className="text-white/60 text-sm mt-2">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
        </div>
        <div className="divide-y divide-white/5 border border-white/10">
          {(items || []).map(b => (
            <div key={b.id} className="p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div className="text-sm">
                <div className="font-heading font-bold">{b.client_name}</div>
                <div className="text-white/60 text-xs">{b.service_label}</div>
              </div>
              <div className="text-xs text-white/70">{b.date} · {b.time}</div>
            </div>
          ))}
          {(!items || items.length === 0) && <div className="p-6 text-center text-white/50 text-sm">Nessun conflitto.</div>}
        </div>
        <div className="mt-4 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost !py-1.5 !text-[11px]">Chiudi</button>
          {onConfirm && (
            <button onClick={onConfirm} data-testid="conflict-confirm" className="btn-primary !py-1.5 !text-[11px]">{confirmLabel || "Conferma"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

function TimeInput({ label, value, onChange, disabled }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">{label}</span>
      <input type="time" disabled={disabled} value={value || ""} onChange={e => onChange(e.target.value)}
        className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none disabled:opacity-40" />
    </label>
  );
}
